import { getSupabase } from './supabase';
import type {
  AppRole, AppUser, AuditExportResult, AuditHistoryRow, AuditStatus, CalendarRow, ClientContact, ClientCrmFields, ClientDrawerData,
  ClientSearchRow, ConfirmadosKpis, ContractionRow, CrmClientRow, CrmKpis, Evidence, FalsePositiveCandidate,
  FalsePositiveKpis, FalsePositiveReviewStatus, FilaKpis, Filters, FunilRow,
  HeatmapRow, LostEvent, Me, Reason, ReasonCategory, Recovered, RelatedEvent, Retirement, SparkPoint, TendenciaRow,
  TopMotivoRow, VisaoGeralKpis, WaterfallStep, MotivosDetalhe,
} from './types';

/** Erro de API já traduzido — carrega o `code` do backend pra UI decidir o que fazer. */
export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function authHeader(): Promise<Record<string, string>> {
  const { data } = await getSupabase().auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new ApiError(401, 'no_session', 'Sessão expirada. Entre novamente.');
  return { Authorization: `Bearer ${token}` };
}

type QueryParams = Record<string, unknown>;

function qs(params: QueryParams): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

async function parse<T>(res: Response): Promise<T> {
  if (res.ok) return (await res.json()) as T;
  let code = 'http_error';
  let message = `Erro ${res.status}.`;
  try {
    const body = await res.json();
    if (body?.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
    }
  } catch {
    /* resposta sem corpo JSON — mantém a mensagem genérica */
  }
  throw new ApiError(res.status, code, message);
}

async function get<T>(path: string, params: QueryParams = {}): Promise<T> {
  const res = await fetch(`/api${path}${qs(params)}`, { headers: await authHeader() });
  return parse<T>(res);
}

async function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    headers: { ...(await authHeader()), 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return parse<T>(res);
}

type Items<T> = { items: T[] };

/**
 * O driver `pg` devolve int8/numeric como STRING ("112"), inclusive as chaves.
 * Os tipos em lib/types declaram `id`/`reason_id` como number, e o backend
 * valida `reason_id` com z.number() — mandar a string de volta num PATCH daria
 * 422. Normalizar aqui, na fronteira, mantém o resto do app livre de coerção.
 */
const num = (v: unknown): number => Number(v);
const numOrNull = (v: unknown): number | null => (v === null || v === undefined ? null : Number(v));

const normEvent = (e: LostEvent): LostEvent => ({ ...e, id: num(e.id), reason_id: numOrNull(e.reason_id), reason_ids: (e.reason_ids ?? []).map(num) });
const normReason = (r: Reason): Reason => ({ ...r, id: num(r.id) });

export const api = {
  // ---- acesso e usuários ---------------------------------------------------
  /** Primeira chamada após o login: 403 `no_access` aqui é o gate da allowlist. */
  me: () => get<Me>('/me'),

  listUsers: () =>
    get<Items<AppUser>>('/users').then((r) => r.items.map((u) => ({ ...u, id: num(u.id) }))),
  inviteUser: (email: string, role: AppRole) =>
    send<AppUser>('POST', '/users', { email, role }).then((u) => ({ ...u, id: num(u.id) })),
  updateUser: (id: number, body: { role?: AppRole; active?: boolean }) =>
    send<AppUser>('PATCH', `/users/${id}`, body).then((u) => ({ ...u, id: num(u.id) })),
  removeUser: (id: number) => send<{ id: number; email: string }>('DELETE', `/users/${id}`),

  // ---- avatar (foto de perfil, self-service) --------------------------------
  uploadAvatar: async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch('/api/me/avatar', { method: 'PUT', headers: await authHeader(), body: form });
    return parse<{ id: number; email: string; role: AppRole; avatar_url: string | null }>(res);
  },
  removeAvatar: () => send<{ removed: boolean }>('DELETE', '/me/avatar'),

  // ---- eventos -------------------------------------------------------------
  listEvents: (filters: Filters, tab: 'fila' | 'confirmados' | 'todos', cursor?: string, limit = 50) =>
    get<{ items: LostEvent[]; next_cursor: string | null }>('/events', { ...filters, tab, cursor, limit })
      .then((r) => ({ ...r, items: r.items.map(normEvent) })),

  auditEvent: (id: number, body: { audit_status: AuditStatus; reason_id?: number | null; reason_ids?: number[]; audit_note?: string | null }) =>
    send<{ id: number; audit_status: AuditStatus; reason_id: number | null; audit_note: string | null; audited_by: string; audited_at: string }>(
      'PATCH', `/events/${id}/audit`, body,
    ),

  eventHistory: (id: number) =>
    get<Items<AuditHistoryRow>>(`/events/${id}/history`)
      .then((r) => r.items.map((h) => ({ ...h, id: num(h.id), reason_id: numOrNull(h.reason_id) }))),

  relatedEvents: (id: number, from?: string, to?: string) =>
    get<Items<RelatedEvent>>(`/events/${id}/related`, { from, to }).then((r) => r.items.map((e) => ({ ...e, id: num(e.id) }))),

  setEventCorrection: (id: number, body: { corrected_event_type: 'churn' | 'contraction'; corrected_mrr_lost: number; note?: string | null }) =>
    send<{ event_id: number; corrected_event_type: string; corrected_mrr_lost: string; note: string | null }>('PUT', `/events/${id}/correction`, body),
  clearEventCorrection: (id: number) => send<{ event_id: number; cleared: boolean }>('DELETE', `/events/${id}/correction`),

  // ---- evidência -----------------------------------------------------------
  // O controller devolve { items: [...] }, não um array puro — ver
  // evidence.controller.ts:listEvidenceHandler.
  listEvidence: (eventId: number) =>
    get<Items<Evidence>>(`/events/${eventId}/evidence`)
      .then((r) => r.items.map((e) => ({ ...e, id: num(e.id), event_id: num(e.event_id) }))),

  addLinkEvidence: (eventId: number, url: string, note?: string) =>
    send<Evidence>('POST', `/events/${eventId}/evidence/link`, { type: 'link', url, note: note || undefined }),

  addImageEvidence: async (eventId: number, file: File, note?: string) => {
    const form = new FormData();
    form.append('file', file);
    if (note) form.append('note', note);
    // Sem Content-Type manual: o browser precisa gerar o boundary do multipart.
    const res = await fetch(`/api/events/${eventId}/evidence/image`, {
      method: 'POST', headers: await authHeader(), body: form,
    });
    return parse<Evidence>(res);
  },

  deleteEvidence: (evidenceId: number) => send<{ id: number }>('DELETE', `/evidence/${evidenceId}`),

  // ---- motivos -------------------------------------------------------------
  listReasons: (includeInactive = false) =>
    get<Items<Reason>>('/reasons', { include_inactive: includeInactive || undefined })
      .then((r) => r.items.map(normReason)),
  createReason: (body: { label: string; description?: string; category: ReasonCategory; is_exception: boolean }) =>
    send<Reason>('POST', '/reasons', body).then(normReason),
  updateReason: (id: number, body: Partial<{ label: string; description: string; category: ReasonCategory; is_exception: boolean; active: boolean }>) =>
    send<Reason>('PATCH', `/reasons/${id}`, body).then(normReason),

  // ---- clientes ------------------------------------------------------------
  searchClients: (q: string, limit = 20) => get<Items<ClientSearchRow>>('/clients/search', { q, limit }).then((r) => r.items),
  clientContacts: (masterId: string) =>
    get<Items<ClientContact>>(`/clients/${encodeURIComponent(masterId)}/contacts`).then((r) => r.items),
  clientDrawer: (masterId: string) => get<ClientDrawerData>(`/clients/${encodeURIComponent(masterId)}`),

  // ---- CRM de clientes (módulo Clientes, PRD §8) ----------------------------
  // `from`/`to` escopam a adimplência ao período — sem eles, usa o histórico
  // inteiro pra achar a fatura mais recente (ver clients-crm.service.ts).
  crmClients: (params: { q?: string; financeiro?: 'em_dia' | 'pendencia' | 'falha'; status_crm?: string; from?: string; to?: string; cursor?: string; limit?: number }) =>
    get<{ items: CrmClientRow[]; next_cursor: string | null }>('/crm/clients', params),
  crmKpis: (params: { from?: string; to?: string } = {}) => get<CrmKpis>('/crm/kpis', params),
  patchClientCrm: (masterId: string, body: Partial<ClientCrmFields>) =>
    send<ClientCrmFields & { master_id: string }>('PATCH', `/crm/clients/${encodeURIComponent(masterId)}`, body),

  // ---- KPIs ----------------------------------------------------------------
  filaKpis: (f: Filters) => get<FilaKpis>('/kpis/fila', { ...f }),
  confirmadosKpis: (f: Filters) => get<ConfirmadosKpis>('/kpis/confirmados', { ...f }),
  visaoGeralKpis: (f: Filters) => get<VisaoGeralKpis>('/kpis/visao-geral', { ...f }),
  sparkline: (f: Filters, metric: 'mrr_lost' | 'count' = 'mrr_lost', days = 14) =>
    get<Items<SparkPoint>>('/kpis/sparkline', { ...f, metric, days }).then((r) => r.items),

  // ---- falso positivo (win-back: churn confirmado que reassinou) -----------
  falsePositives: (period?: { from?: string; to?: string }) =>
    get<Items<FalsePositiveCandidate>>('/false-positives', period).then((r) => r.items.map((c) => ({ ...c, id: num(c.id) }))),
  falsePositivesKpis: (period?: { from?: string; to?: string }) => get<FalsePositiveKpis>('/false-positives/kpis', period),
  reviewFalsePositive: (eventId: number, status: FalsePositiveReviewStatus, note?: string) =>
    send<FalsePositiveCandidate>('POST', `/false-positives/${eventId}/review`, { status, note: note || undefined }),

  // ---- gráficos ------------------------------------------------------------
  tendencia: (f: Filters, granularity = 'month') =>
    get<Items<TendenciaRow>>('/charts/tendencia', { ...f, granularity }).then((r) => r.items),
  waterfall: (f: Filters) => get<Items<WaterfallStep>>('/charts/waterfall', { ...f }).then((r) => r.items),
  funil: (f: Filters) => get<Items<FunilRow>>('/charts/funil', { ...f }).then((r) => r.items),
  topMotivos: (f: Filters) =>
    get<Items<TopMotivoRow>>('/charts/top-motivos', { ...f })
      .then((r) => r.items.map((m) => ({ ...m, reason_id: num(m.reason_id) }))),
  contractionVsMrr: (f: Filters, granularity = 'month') =>
    get<Items<ContractionRow>>('/charts/contraction-vs-mrr-pendente', { ...f, granularity }).then((r) => r.items),
  heatmap: (f: Filters) => get<Items<HeatmapRow>>('/charts/heatmap-zona-categoria', { ...f }).then((r) => r.items),
  calendario: (f: Filters) => get<Items<CalendarRow>>('/charts/calendario', { ...f }).then((r) => r.items),
  motivosDetalhe: (f: Filters) =>
    get<{ items: MotivosDetalhe }>('/charts/motivos-detalhe', { ...f }).then((r) => {
      const d = r.items;
      return {
        ...d,
        ranking: d.ranking.map((m) => ({ ...m, reason_id: num(m.reason_id) })),
        pares: d.pares.map((p) => ({ ...p, a_id: num(p.a_id), b_id: num(p.b_id) })),
        tendencia: d.tendencia.map((t) => ({ ...t, reason_id: num(t.reason_id) })),
        zonas: d.zonas.map((z) => ({ ...z, reason_id: num(z.reason_id) })),
      };
    }),

  // ---- retirado pela fonte (de-para, 2026-09-14) ----------------------------
  // Histórico de eventos cuja natural_key a Nekt parou de reconhecer — nunca
  // é decisão de auditor, é constatação técnica. Alimenta o disclaimer da
  // Visão Geral, pra explicar por que a Perda Bruta pode variar sem ninguém
  // ter tocado em nada.
  retirements: (period?: { from?: string; to?: string }) =>
    get<Items<Retirement>>('/retirements', period).then((r) => r.items),

  // ---- recuperados (2026-09-14) ----------------------------------------------
  // Receita que voltou no período: fatura com falha de cobrança já paga, ou
  // churn cujo cliente abriu assinatura nova depois. Endpoint único devolve
  // as duas listas + os KPIs já somados (as listas são pequenas, não vale
  // uma 3ª chamada só pra total).
  recovered: (period?: { from?: string; to?: string; channel?: string }) => get<Recovered>('/recovered', period),

  // ---- exportação de auditoria pra Nekt (2026-09-15) ------------------------
  // Botão admin-only: manda o universo completo do período (perda confirmada
  // + anulado + VDI) pro Webhook Source `webhook-6Pgb` — a Nekt deduplica por
  // evento do lado dela, então reexportar um período sobreposto é seguro.
  exportAudit: (period: { from: string; to: string }) =>
    send<AuditExportResult>('POST', '/audit-export', period),
};
